import type { PaymentTransactionViewModel } from '@/components/settings/billing/payment-transaction-view-model';
import type { PricingAccessSnapshot } from '@/lib/pricing/access-snapshot';
import type { BillingState } from '@/lib/pricing/billing-state';
import type { PaidAccessState } from '@/lib/pricing/paid-access';
import {
  getUserFacingReadRetryDelay,
  retryUserFacingReadQuery,
} from '@/lib/query/retry-policy';
import {
  type QueryClient,
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';

type PaymentApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

async function readPaymentApiJson<T>(
  response: Response,
  fallbackError: string
): Promise<T> {
  const result = (await response.json()) as PaymentApiResponse<T>;
  if (!response.ok || !result.success) {
    throw new Error(result.error || fallbackError);
  }

  if (typeof result.data === 'undefined') {
    throw new Error(`${fallbackError}: missing data`);
  }

  return result.data;
}

async function fetchPricingAccessSnapshotFromApi(): Promise<PricingAccessSnapshot> {
  const response = await fetch('/api/payment/access-snapshot', {
    method: 'GET',
    credentials: 'include',
  });

  return readPaymentApiJson(response, 'Failed to fetch payment access');
}

async function fetchPaymentTransactionsFromApi(input: {
  pageIndex: number;
  pageSize: number;
  search: string;
  sorting: SortingState;
}) {
  const response = await fetch('/api/payment/transactions', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  return readPaymentApiJson<{
    items: PaymentTransactionViewModel[];
    total: number;
  }>(response, 'Failed to fetch payment transactions');
}

// Query keys
export const paymentKeys = {
  all: ['payment'] as const,
  accessSnapshot: (userId: string) =>
    [...paymentKeys.all, 'accessSnapshot', userId] as const,
  paymentTransactions: () =>
    [...paymentKeys.all, 'paymentTransactions'] as const,
  paymentTransactionsList: (filters: {
    pageIndex: number;
    pageSize: number;
    search: string;
    sorting: SortingState;
  }) => [...paymentKeys.paymentTransactions(), filters] as const,
};

export async function refreshPaymentQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: paymentKeys.all,
    refetchType: 'active',
  });
}

function usePricingAccessSnapshot<TData>(
  userId: string | undefined,
  options: { enabled?: boolean } | undefined,
  select: (snapshot: PricingAccessSnapshot) => TData
) {
  return useQuery({
    queryKey: paymentKeys.accessSnapshot(userId || ''),
    queryFn: fetchPricingAccessSnapshotFromApi,
    enabled: Boolean(userId) && (options?.enabled ?? true),
    select,
    staleTime: Number.POSITIVE_INFINITY,
    retry: retryUserFacingReadQuery,
    retryDelay: getUserFacingReadRetryDelay,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

// Hook to get current plan with subscription status
export function useCurrentPlan(
  userId: string | undefined,
  options?: { enabled?: boolean }
) {
  return usePricingAccessSnapshot<BillingState>(
    userId,
    options,
    (snapshot) => snapshot.billingState
  );
}

export function usePaidAccess(
  userId: string | undefined,
  options?: { enabled?: boolean }
) {
  return usePricingAccessSnapshot<PaidAccessState>(
    userId,
    options,
    (snapshot) => snapshot.paidAccessState
  );
}

export function usePaymentTransactions(
  pageIndex: number,
  pageSize: number,
  search: string,
  sorting: SortingState
) {
  return useQuery({
    queryKey: paymentKeys.paymentTransactionsList({
      pageIndex,
      pageSize,
      search,
      sorting,
    }),
    queryFn: async () => {
      const result = await fetchPaymentTransactionsFromApi({
        pageIndex,
        pageSize,
        search,
        sorting,
      });

      return {
        items: result.items || [],
        total: result.total || 0,
      };
    },
    placeholderData: keepPreviousData,
  });
}
